import { Routes } from '@angular/router';
import { permissionGuard } from 'src/lib/guards/permission.guard';
import { roleGuard } from '../lib/guards/admin-role.guard';
import { authGuard } from '../lib/guards/auth.guard';
import { boardRoutes } from './features/board/board.routes';
import { featuredMemberRoutes } from './features/featured-member/featured-member.routes';
import { termsRoutes } from './features/terms/terms.routes';
import { depositBankRoutes } from './features/deposit-bank/deposit-bank.routes';
import { exchangeRateRoutes } from './features/exchange-rate/exchange-rate.routes';
import { adminRoutes } from './features/admin/admin.routes';
import { brandRoutes } from './features/brand/brand.routes';
import { agentsRoutes } from './features/agents/agents.routes';
import { globalMarketerRoutes } from './features/global-marketer/global-marketer.routes';
import { flightsRoutes } from './features/flights/flights.routes';
import { liveChatRoutes } from './features/live-chat/live-chat.routes';
import { hotelRoutes } from './features/hotel/hotel.routes';
import { restaurantRoutes } from './features/restaurant/restaurant.routes';
import { attractionRoutes } from './features/attraction/attraction.routes';
import { guideRoutes } from './features/guide/guide.routes';
import { carRoutes } from './features/car/car.routes';
import { farmRoutes } from './features/farm/farm.routes';
import { mrhstRoutes } from './features/mrhst/mrhst.routes';
import { batchRoutes } from './features/batch/batch.routes';
import { batchLogRoutes } from './features/batch-log/batch-log.routes';
import { aiChatSessionRoutes } from './features/ai-chat-session/ai-chat-session.routes';
import { codemasterRoutes } from './features/codemaster/codemaster.routes';
import { groupingNameRoutes } from './features/grouping-name/grouping-name.routes';
import { systemLogRoutes } from './features/system-log/system-log.routes';
import { orderingAgencyRoutes } from './features/ordering-agency/ordering-agency.routes';
import { packagegoodsRoutes } from './features/packagegoods/packagegoods.routes';
import { depositRoutes } from './features/deposit/deposit.routes';
import { documentRoutes } from './features/document/document.routes';
import { estimateTemplateRoutes } from './features/estimate-template/estimate-template.routes';
import { holidayCalendarRoutes } from './features/holiday-calendar/holiday-calendar.routes';
import { ecGoodsGrpRoutes } from './features/ec-goods-grp/ec-goods-grp.routes';
import { ecGoodsRoutes } from './features/ec-goods/ec-goods.routes';
import { homepageBuilderBookingSettingsRoutes } from './features/homepage-builder-booking-settings/homepage-builder-booking-settings.routes';
import { homepageMenuRoutes } from './features/homepage-menu/homepage-menu.routes';
import { homepageNavRoutes } from './features/homepage-nav/homepage-nav.routes';
import { menuScreenRoutes } from './features/menu-screen/menu-screen.routes';
import { menuScreenPresetRoutes } from './features/menu-screen-preset/menu-screen-preset.routes';
import { aiProviderConfigRoutes } from './features/ai-provider-config/ai-provider-config.routes';
import { aiFeatureProviderRoutes } from './features/ai-feature-provider/ai-feature-provider.routes';
import { homepageBuilderRoutes } from './features/homepage-builder/homepage-builder.routes';
import { screenPartRoutes } from './features/screen-part/screen-part.routes';
import { screenPartDefinitionRoutes } from './features/screen-part-definition/screen-part-definition.routes';
import { screenLayoutRoutes } from './features/screen-layout/screen-layout.routes';
// portal-layout-redesign Phase 1.5 — 신규 파트너 타입 마스터 (partition 시스템 대체 예정)
import { partnerTypeRoutes } from './features/partner-type/partner-type.routes';
import { partnerGroupRoutes } from './features/partner-group/partner-group.routes';
// portal-layout-redesign Phase 2 — 통합 포털 레이아웃 페이지 (3-탭)
import { portalLayoutRoutes } from './features/portal-layout/portal-layout.routes';
import { dashboardRoutes } from './features/dashboard/dashboard.routes';
import { bookingFitDocumentRoutes } from './features/booking-fit-document/booking-fit-document.routes';
import { userInfoRoutes } from './features/user-info/user-info.routes';
import { loginRoutes } from './features/login/login.routes';
import { partnersRoutes } from './features/partners/partners.routes';
import { chapterRoutes } from './features/chapter/chapter.routes';
import { sendEmailRoutes } from './features/send-email/send-email.routes';
import { pickupRoutes } from './features/pickup/pickup.routes';
import { statsRoutes } from './features/stats/stats.routes';
import { calculationRoutes } from './features/calculation/calculation.routes';
import { emailMsgTemplateRoutes } from './features/email-msg-template/email-msg-template.routes';

export const routes: Routes = [
  // Login (Modern - features/login)
  // URL: /login, /loginPartner, /loginAgents, /logout
  ...loginRoutes,
  {
    path: 'message-chat',
    title: 'Chat',
    loadComponent: () =>
      import(
        './features/live-chat/components/smart/live-chat-chat-page/live-chat-chat-page.component'
      ).then((m) => m.LiveChatChatPageComponent),
  },
  {
    path: 'message-chat-agent',
    title: 'Chat',
    loadComponent: () =>
      import(
        './features/live-chat/components/smart/live-chat-chat-agent-page/live-chat-chat-agent-page.component'
      ).then((m) => m.LiveChatChatAgentPageComponent),
  },
  // Send Email (Modern - features/send-email)
  // URL: /send-email
  ...sendEmailRoutes,
  // Document (Modern - features/document)
  // URL: /document/*
  // Component: features/document/components/print-layout/print-layout
  ...documentRoutes,
  {
    path: '',
    loadComponent: () =>
      import('./components/shared/main-layout/main-layout.component').then(
        (m) => m.MainLayoutComponent,
      ),
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },

      {
        path: 'partners-admin',
        canActivate: [roleGuard('PARTNER')],
        loadChildren: () =>
          import('./features/partners-portal/partners-portal.routes').then(
            (m) => m.partnersPortalRoutes,
          ),
      },

      {
        path: 'agents-admin',
        canActivate: [roleGuard('AGENT')],
        loadChildren: () =>
          import('./features/agents-portal/agents-portal.routes').then(
            (m) => m.agentsPortalRoutes,
          ),
      },

      // 대시보드
      ...dashboardRoutes,
      { path: 'home', redirectTo: 'dashboard', pathMatch: 'prefix' },

      // 통계 (Modern - features/stats)
      // stats/status, status-multiple, calendar, calendar-annual, sold-out-calendar, item, flights, calc
      ...statsRoutes,
      // 문의 (메세지) - Modern Live Chat
      ...liveChatRoutes,
      // qna legacy alias -> Live Chat
      {
        path: 'qna',
        redirectTo: 'live-chat',
        pathMatch: 'full',
      },
      {
        path: 'qna/list',
        redirectTo: 'live-chat/list',
        pathMatch: 'full',
      },
      {
        path: 'qna/list/:id',
        redirectTo: 'live-chat/list/:id',
        pathMatch: 'full',
      },
      // mail alias → Live Chat list
      {
        path: 'mail',
        redirectTo: 'live-chat/list',
        pathMatch: 'full',
      },
      // qna-agents alias → Live Chat list
      {
        path: 'qna-agents',
        redirectTo: 'live-chat/list',
        pathMatch: 'full',
      },
      {
        path: 'mail-agents',
        redirectTo: 'live-chat/list',
        pathMatch: 'full',
      },
      // 게시판 (Modern - features/board)
      // URL: /notice, /event, /faq, /column, /review, /bbs/cms, /bbs/hmpg, /bbs/popup
      // Component: features/board/components/smart/board-list-page
      ...boardRoutes,
      // 이달의 위원 (BBS 와 완전 분리된 신규 도메인)
      // URL: /featured-member
      // Component: features/featured-member/components/smart/featured-member-list-page
      ...featuredMemberRoutes,
      // Legacy BBS 라우트 제거됨 (Modern Board로 통합)
      // 회원 관리
      ...userInfoRoutes,
      // 선불 내역/충전 (Modern - features/prpay/chrg)
      {
        path: 'prpay/chrg',
        loadChildren: () =>
          import('./features/prpay/chrg/chrg.routes').then((m) => m.chrgRoutes),
      },
      // 선불 내역/환전 (Modern - features/prpay/exchange)
      {
        path: 'prpay/exchange',
        loadChildren: () =>
          import('./features/prpay/exchange/exchange.routes').then(
            (m) => m.exchangeRoutes,
          ),
      },
      // 선불 내역/사용 (Modern - features/prpay/use)
      {
        path: 'prpay/use',
        loadChildren: () =>
          import('./features/prpay/use/use.routes').then((m) => m.useRoutes),
      },
      // BtoB 예약 (독립 feature - features/btob-booking)
      {
        path: 'btob-booking',
        canActivate: [permissionGuard('BOOKING_FIT$READ')],
        loadChildren: () =>
          import('./features/btob-booking/btob-booking.routes').then(
            (m) => m.btobBookingRoutes,
          ),
      },
      // 기존 URL 호환: booking-fit → btob-booking 리다이렉트
      {
        path: 'booking-fit/list/:id',
        redirectTo: 'btob-booking/list/:id',
        pathMatch: 'full',
      },
      {
        path: 'booking-fit/list',
        redirectTo: 'btob-booking/list',
        pathMatch: 'full',
      },
      { path: 'booking-fit', redirectTo: 'btob-booking', pathMatch: 'full' },
      // 기존 URL 호환: booking-fit/booking-estimate → btob-booking 리다이렉트
      {
        path: 'booking-fit/booking-estimate',
        redirectTo: 'btob-booking',
        pathMatch: 'prefix',
      },
      // 자유 여행 상담 (Modern - features/requests)
      {
        path: 'booking/requests-fit',
        loadChildren: () =>
          import('./features/requests/requests.routes').then(
            (m) => m.requestsRoutes,
          ),
        canActivate: [permissionGuard('REQUESTS$READ_FIT')],
      },
      // 자유 여행 상담 (뷰티) - Config-Driven 통합
      {
        path: 'booking-beauty/requests',
        loadChildren: () =>
          import('./features/requests/requests.routes').then(
            (m) => m.requestsBeautyRoutes,
          ),
        canActivate: [permissionGuard('REQUESTS$READ')],
      },
      // 패키지 견적 (Modern - features/booking-estimate)
      {
        path: 'booking/requests',
        loadChildren: () =>
          import('./features/booking-estimate/booking-estimate.routes').then(
            (m) => m.bookingEstimatePackageRoutes,
          ),
        canActivate: [permissionGuard('REQUESTS$READ')],
      },
      // Booking FIT Document Download (Modern - features/booking-fit-document)
      ...bookingFitDocumentRoutes.map((r) => ({
        ...r,
        canActivate: [permissionGuard('BOOKING_FIT$READ')],
      })),
      // 예약 BEAUTY (Modern - features/booking)
      {
        path: 'booking-beauty',
        canActivate: [permissionGuard('BOOKING_BEAUTY$READ')],
        loadChildren: () =>
          import('./features/booking/booking.routes').then(
            (m) => m.bookingBeautyRoutes,
          ),
      },
      // 고객 상담 (Modern - features/requests)
      {
        path: 'requests',
        loadChildren: () =>
          import('./features/requests/requests.routes').then(
            (m) => m.requestsRoutes,
          ),
        canActivate: [permissionGuard('REQUESTS$READ')],
      },
      // 예약 TOUR (Modern - features/booking)
      {
        path: 'booking',
        loadChildren: () =>
          import('./features/booking/booking.routes').then(
            (m) => m.bookingTourRoutes,
          ),
      },
      // 예약 HOTEL (Modern - features/booking) — hmpg 호텔 단독 예약(bookingType HOTEL) 목록
      {
        path: 'booking-etc/hotel',
        canActivate: [permissionGuard('BOOKING$READ', 'BOOKING$READ_HOTEL')],
        loadChildren: () =>
          import('./features/booking/booking.routes').then(
            (m) => m.bookingHotelRoutes,
          ),
      },
      {
        path: 'prepare/hotel',
        loadChildren: () =>
          import(
            './features/booking-prepare-hotel/booking-prepare-hotel.routes'
          ).then((m) => m.bookingPrepareHotelRoutes),
        canActivate: [permissionGuard('BOOKING$READ_PREPARE_HOTEL')],
      },
      // 숙박 (Legacy - components/hotel, hotel-graph, hotel-room)
      {
        path: 'hotel/assign',
        loadChildren: () =>
          import('./components/hotel/hotel.module').then((m) => m.HotelModule),
        canActivate: [permissionGuard('HOTEL_ASSIGN$READ')],
      },
      {
        path: 'hotel/graph',
        loadComponent: () =>
          import('./components/hotel-graph/hotel-graph.component').then(
            (m) => m.HotelGraphComponent,
          ),
        canActivate: [permissionGuard('HOTEL_ASSIGN$READ')],
      },
      {
        path: 'hotel/room',
        loadChildren: () =>
          import('./components/hotel-room/hotel-room.module').then(
            (m) => m.HotelRoomModule,
          ),
        canActivate: [permissionGuard('HOTEL_ASSIGN$READ')],
      },
      // 입금 (Modern - features/deposit)
      // URL: /deposit (Legacy와 동일)
      // Component: features/deposit/components/smart/deposit-list-page
      ...depositRoutes,

      // 입금/은행 (Modern - features/deposit-bank)
      // URL: /deposit/bank (Legacy와 동일)
      // Component: features/deposit-bank/components/smart/deposit-bank-list-page
      ...depositBankRoutes,

      // 송영/목록
      ...pickupRoutes,
      // Flights (Modern - features/flights)
      // URL: /master/flights
      // Component: features/flights/components/smart/flights-list-page
      ...flightsRoutes,

      // Calculation (Modern - features/calculation)
      ...calculationRoutes,
      // Homepage Nav (Modern - features/homepage-nav) — 메뉴/네비게이션
      ...homepageNavRoutes,
      // Homepage Menu (Modern - features/homepage-menu)
      ...homepageMenuRoutes,
      // Menu Screen (Modern - features/menu-screen)
      ...menuScreenRoutes,
      // Menu Screen Preset (features/menu-screen-preset)
      ...menuScreenPresetRoutes,
      ...aiProviderConfigRoutes,
      ...aiFeatureProviderRoutes,
      // Homepage Builder (Modern - features/homepage-builder)
      ...homepageBuilderRoutes,
      // Screen Part (Modern - features/screen-part) — 파트 인스턴스
      ...screenPartRoutes,
      // Screen Part Definition (Modern - features/screen-part-definition) — 파트 정의
      ...screenPartDefinitionRoutes,
      // Screen Layout (Modern - features/screen-layout)
      // 패키지/상품/예약 화면 레이아웃 관리
      ...screenLayoutRoutes,
      // Partner Type 마스터 (portal-layout-redesign Phase 1.5)
      // 브랜드별 파트너 타입(SELLER/BUYER/COMMITTEE 등) 정의·편집
      {
        path: 'partner-type',
        children: partnerTypeRoutes,
      },
      // Partner Group 마스터 (PartnerGroup 도입 v1.3 Phase 4)
      // 사이트별 파트너 노출 큐레이션을 위한 속성(태그) 분류
      {
        path: 'partner-group',
        children: partnerGroupRoutes,
      },
      // Portal Layout 통합 페이지 (portal-layout-redesign Phase 2)
      // 파트너/고객/마케터 포털 레이아웃 통합 관리 (3-탭)
      {
        path: 'portal-layout',
        children: portalLayoutRoutes,
      },
      // Homepage Builder Booking Settings (Modern - features/homepage-builder-booking-settings)
      ...homepageBuilderBookingSettingsRoutes,
      // Brand (Modern - features/brand)
      // URL: /master/brand
      // Component: features/brand/components/smart/brand-list-page
      ...brandRoutes,

      // Agents (Modern - features/agents)
      // URL: /master/agents
      // Component: features/agents/components/smart/agents-list-page
      ...agentsRoutes,
      ...globalMarketerRoutes,

      // 마스터/에이전트지점 (agents detail 페이지 내 branch 관리로 통합됨)
      // 마스터/발주처
      ...orderingAgencyRoutes,
      // 마스터/약관 (Modern - features/terms)
      ...termsRoutes,
      // 마스터/설문 폼 (Modern - features/consultation-form)
      {
        path: 'requests/consultation-form',
        loadChildren: () =>
          import('./features/consultation-form/consultation-form.routes').then(
            (m) => m.consultationFormRoutes,
          ),
      },
      // 마스터/설문 메뉴 (Modern - features/consultation-menu)
      {
        path: 'requests/consultation-menu',
        loadChildren: () =>
          import('./features/consultation-menu/consultation-menu.routes').then(
            (m) => m.consultationMenuRoutes,
          ),
      },
      // AI 상담 세션 관리 (Modern - features/ai-chat-session)
      ...aiChatSessionRoutes,
      // 마스터/자동 배정 규칙 (Modern - features/auto-assign-rule)
      {
        path: 'master/auto-assign-rule',
        loadChildren: () =>
          import('./features/auto-assign-rule/auto-assign-rule.routes').then(
            (m) => m.autoAssignRuleRoutes,
          ),
      },
      // 마스터/호텔 (Modern - features/hotel)
      // mrhst에서 호텔 전용으로 독립 분리
      ...hotelRoutes,
      // 마스터/식당 (Modern - features/restaurant)
      // mrhst에서 식당 전용으로 독립 분리
      ...restaurantRoutes,
      // 마스터/관광지 (Modern - features/attraction)
      // mrhst에서 관광지 전용으로 독립 분리
      ...attractionRoutes,
      // 마스터/가이드 (Modern - features/guide)
      // mrhst에서 가이드 전용으로 독립 분리
      ...guideRoutes,
      // 마스터/렌터카 (Modern - features/car)
      // mrhst에서 렌터카 전용으로 독립 분리
      ...carRoutes,
      // 마스터/농원 (Modern - features/farm)
      // mrhst에서 농원 전용으로 독립 분리
      ...farmRoutes,
      // 마스터/시설 관리 (Modern - features/mrhst)
      // 10개 시설 유형 통합: hotel, car, attraction, schedule, restaurant, guide, farm, instructor, branch, sponsorship
      // 상품(Goods)은 상세 페이지 내 섹션으로 통합 (별도 라우트 불필요)
      ...mrhstRoutes,
      // 마스터/패키지상품 (Modern - features/packagegoods)
      ...packagegoodsRoutes,
      // 마스터/적용환율 (Modern - features/exchange-rate)
      // URL: /master/exchange-rate
      // Component: features/exchange-rate/components/smart/exchange-rate-list-page
      ...exchangeRateRoutes,
      // 마스터/이메일 메시지 템플릿
      ...emailMsgTemplateRoutes,
      // 마스터/견적 템플릿
      ...estimateTemplateRoutes,
      // 마스터/공휴일 캘린더 (견적 템플릿 날짜 조건이 참조)
      ...holidayCalendarRoutes,
      // 마스터/그룹코드 (Modern Config-Driven)
      ...groupingNameRoutes,
      // 시스템/코드마스터 (Modern Config-Driven)
      ...codemasterRoutes,
      // 페이워드관리/배치 (Modern Config-Driven)
      ...batchRoutes,
      // 패이워드관리/배치로그 (Modern Config-Driven)
      ...batchLogRoutes,
      // 패이워드관리/시스템로그 (Modern - features/system-log)
      ...systemLogRoutes,
      // 시스템/관리자 (Modern - features/admin)
      // URL: /paywith-master/admin
      // Component: features/admin/components/smart/admin-list-page
      {
        path: 'paywith-master/admin',
        children: adminRoutes,
        canActivate: [roleGuard('ADMIN_SYSTEM')],
      },

      // 전자상거래/상품그룹 (Modern - features/ec-goods-grp)
      ...ecGoodsGrpRoutes,
      // 전자상거래/상품 (Modern - features/ec-goods)
      ...ecGoodsRoutes,
      // 파트너스 (Modern - features/partners)
      ...partnersRoutes,
      // OKTA 지회 관리 (Chapter)
      ...chapterRoutes,
      // 다이렉트 예약 (Phase 6 scaffold)
      {
        path: 'direct-booking',
        loadChildren: () =>
          import('./features/direct-booking/direct-booking.routes').then(
            (m) => m.directBookingRoutes,
          ),
      },
      {
        path: 'requests/alarm-list',
        loadChildren: () =>
          import('./features/request-alarms/request-alarms.routes').then(
            (m) => m.requestAlarmsRoutes,
          ),
      },
      {
        path: 'requests/reviews-partners',
        loadChildren: () =>
          import('./features/review-partner/review-partner.routes').then(
            (m) => m.reviewPartnerRoutes,
          ),
      },
      {
        path: 'requests/reviews',
        loadChildren: () =>
          import('./features/review-request/review-request.routes').then(
            (m) => m.reviewRequestRoutes,
          ),
      },
    ],
  },
  { path: '**', redirectTo: 'dashboard' },
];
